import { usePropertySets } from '../../../views/CustomProperties/PropertySets/queries';
import React, { useEffect, useState } from 'react';
import { PropertySet } from '../../../views/CustomProperties/PropertySets/types';
import ZuulLoader from '../../ZuulLoader/ZuulLoader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DataObjectOutlined from '@mui/icons-material/DataObjectOutlined';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@/ui';
import { CustomProperties } from './CustomProperties';
import {
  CommonCustomPropertiesProps,
  CustomPropertiesCardProps,
  CustomPropertiesModalProps,
  CustomPropertiesSetupProps,
  CustomPropertiesSummaryProps,
} from './types';
import CollapsibleCard from '../../CollapsibleCard/CollapsibleCard';
import ExportPropertySetsButton from '../PropertySets/modal/ExportPropertySetsButton';
import ImportPropertySetsButton from '../PropertySets/modal/ImportPropertySetsButton';

const CustomPropertiesSetup = ({
  propertySetsIds,
  propertySetsValues,
  setPropertySetsIds,
  setPropertySetsValues,
  type,
  disabled,
  title,
  description,
  defaultOpen,
}: CustomPropertiesSetupProps) => {
  const { data: existingPropertySets, isLoading: isPropertySetLoading } = usePropertySets(propertySetsIds, false);

  const [propertySets, setPropertySets] = useState<PropertySet[]>([]);

  const removePropertySet = (propertySetToRemove: PropertySet) => {
    setPropertySets(propertySets.filter((propertySet) => propertySetToRemove.id !== propertySet.id));
    setPropertySetsIds(propertySetsIds.filter((id) => id !== propertySetToRemove.id));
    const { [propertySetToRemove.replacementKey]: _, ...newPropertySetsValues } = propertySetsValues;
    setPropertySetsValues(newPropertySetsValues);
  };

  useEffect(() => {
    if (existingPropertySets && propertySetsIds?.length !== 0) {
      setPropertySets(existingPropertySets);
    } else {
      setPropertySets([]);
    }
  }, [existingPropertySets, isPropertySetLoading, propertySetsIds]);

  const commonProps: CommonCustomPropertiesProps = {
    propertySetsIds,
    propertySetsValues,
    setPropertySetsIds,
    setPropertySetsValues,
    removePropertySet,
    setPropertySets,
    propertySets,
    isPropertySetLoading,
  };

  const renderCustomProperties = () => {
    switch (type) {
      case 'modal':
        return <CustomPropertiesSetup.Modal disabled={disabled || false} {...commonProps} />;
      case 'summary':
        return <CustomPropertiesSetup.Summary title={title || ''} description={description || ''} {...commonProps} />;
      case 'card':
        return <CustomPropertiesSetup.Card defaultOpen={defaultOpen || false} {...commonProps} />;
      default:
        return <CustomPropertiesSetup.Card {...commonProps} />;
    }
  };

  return <>{renderCustomProperties()}</>;
};

CustomPropertiesSetup.Modal = ({
  propertySetsIds,
  propertySetsValues,
  setPropertySetsIds,
  setPropertySetsValues,
  disabled,
  removePropertySet,
  setPropertySets,
  propertySets,
  isPropertySetLoading,
}: CustomPropertiesModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  return (
    <>
      <Button color='primary' onClick={() => setIsOpen(true)} disabled={disabled} startIcon={<DataObjectOutlined />}>
        Custom Properties
      </Button>
      {/* Wider than any named size: the tree and the details pane sit side by side inside. */}
      <Modal isOpen={isOpen} maxWidth={false} sx={{ '& .MuiDialog-paper': { maxWidth: '75vw' } }}>
        <ModalHeader>
          <Stack direction='row' spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
            <Typography variant='h6' component='h3' sx={{ flexGrow: 1 }}>
              Custom Properties
            </Typography>
            <ImportPropertySetsButton
              setPropertySetsIds={setPropertySetsIds}
              setPropertySetsValues={setPropertySetsValues}
              propertySetsIds={propertySetsIds}
            />
            <ExportPropertySetsButton propertySetsValues={propertySetsValues} />
          </Stack>
        </ModalHeader>
        <ModalBody>
          {isPropertySetLoading ? (
            <ZuulLoader text={'Loading Custom Properties'} />
          ) : (
            <CustomProperties
              propertySetsIds={propertySetsIds}
              propertySets={propertySets}
              propertySetsValues={propertySetsValues}
              setPropertySetsValues={setPropertySetsValues}
              removePropertySet={removePropertySet}
              setPropertySets={setPropertySets}
              setPropertySetsIds={setPropertySetsIds}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

CustomPropertiesSetup.Summary = ({
  propertySetsIds,
  propertySetsValues,
  setPropertySetsIds,
  setPropertySetsValues,
  removePropertySet,
  setPropertySets,
  propertySets,
  isPropertySetLoading,
  title,
  description,
}: CustomPropertiesSummaryProps) => {
  return (
    <>
      <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
        {/* The theme's largest heading. It replaces Bootstrap's .display-4, which at ~42px sat
            well outside this scale — page titles here top out at 2rem. */}
        <Typography variant='h1' component='h1' sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Stack direction='row' spacing={1}>
          <ImportPropertySetsButton
            setPropertySetsIds={setPropertySetsIds}
            setPropertySetsValues={setPropertySetsValues}
            propertySetsIds={propertySetsIds}
          />
          <ExportPropertySetsButton propertySetsValues={propertySetsValues} />
        </Stack>
      </Stack>
      <Typography variant='body1' sx={{ mt: 0.5 }}>
        {description}
      </Typography>
      <Divider sx={{ my: 2 }} />
      {isPropertySetLoading ? (
        <ZuulLoader text={'Loading Custom Properties'} />
      ) : (
        <CustomProperties
          propertySetsIds={propertySetsIds}
          propertySets={propertySets}
          propertySetsValues={propertySetsValues}
          setPropertySetsValues={setPropertySetsValues}
          removePropertySet={removePropertySet}
          setPropertySets={setPropertySets}
          setPropertySetsIds={setPropertySetsIds}
        />
      )}
    </>
  );
};

CustomPropertiesSetup.Card = ({
  propertySetsIds,
  propertySetsValues,
  setPropertySetsIds,
  setPropertySetsValues,
  removePropertySet,
  setPropertySets,
  propertySets,
  isPropertySetLoading,
  defaultOpen,
}: CustomPropertiesCardProps) => {
  return (
    <CollapsibleCard
      title={'Custom Properties'}
      defaultOpen={defaultOpen || false}
      headerButton={
        <Stack direction='row' spacing={1}>
          <ImportPropertySetsButton
            setPropertySetsIds={setPropertySetsIds}
            setPropertySetsValues={setPropertySetsValues}
            propertySetsIds={propertySetsIds}
          />
          <ExportPropertySetsButton propertySetsValues={propertySetsValues} />
        </Stack>
      }
    >
      {isPropertySetLoading ? (
        <ZuulLoader text={'Loading Custom Properties'} />
      ) : (
        <CustomProperties
          propertySetsIds={propertySetsIds}
          propertySets={propertySets}
          propertySetsValues={propertySetsValues}
          setPropertySetsValues={setPropertySetsValues}
          removePropertySet={removePropertySet}
          setPropertySets={setPropertySets}
          setPropertySetsIds={setPropertySetsIds}
        />
      )}
    </CollapsibleCard>
  );
};

export default CustomPropertiesSetup;
